import { z } from 'zod'

/**
 * Client-side validation mirroring the server rules.
 *
 * These exist to give fast feedback in the form, not to enforce anything —
 * the server revalidates everything. Where the two disagree, the server wins.
 */

export const loginSchema = z.object({
  email: z.string().min(1, 'Enter your email or phone number'),
  password: z.string().min(1, 'Enter your password'),
})

export const registerSchema = z
  .object({
    first_name: z.string().min(1, 'Enter your first name').max(80),
    last_name: z.string().max(80).optional(),
    email: z.string().email('Enter a valid email address').optional().or(z.literal('')),
    phone: z.string().max(32).optional().or(z.literal('')),
    password: z
      .string()
      .min(10, 'Use at least 10 characters')
      .regex(/[A-Za-z]/, 'Include at least one letter')
      .regex(/[0-9]/, 'Include at least one number'),
    password_confirmation: z.string(),
    role: z.enum(['candidate', 'employer', 'freelancer']),
    terms_accepted: z.literal(true, {
      errorMap: () => ({ message: 'Accept the terms to continue' }),
    }),
  })
  .refine((d) => d.password === d.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  })
  // Mirrors the server's required_without pair: one contact route is enough.
  .refine((d) => Boolean(d.email) || Boolean(d.phone), {
    message: 'Enter either an email address or a phone number',
    path: ['email'],
  })

export const jobSchema = z
  .object({
    title: z.string().min(1, 'Enter a job title').max(180),
    description: z.string().min(1, 'Describe the role').max(20000),
    responsibilities: z.string().max(10000).optional(),
    requirements: z.string().max(10000).optional(),
    job_category_id: z.number().int().optional(),
    employment_type: z.enum(['full_time', 'part_time', 'contract', 'internship', 'temporary']),
    work_mode: z.enum(['onsite', 'hybrid', 'remote']),
    experience_min_months: z.number().int().min(0).max(600).optional(),
    experience_max_months: z.number().int().min(0).max(600).optional(),
    salary_min: z.number().min(0).optional(),
    salary_max: z.number().min(0).optional(),
    salary_currency: z.string().length(3).optional(),
    salary_period: z.enum(['hour', 'day', 'week', 'month', 'year']).optional(),
    is_salary_visible: z.boolean().optional(),
    vacancies: z.number().int().min(1).max(9999).optional(),
    location_id: z.number().int().optional(),
    location_ids: z.array(z.number().int()).max(20).optional(),
    skill_ids: z.array(z.number().int()).max(30).optional(),
  })
  .refine((d) => !d.salary_min || !d.salary_max || d.salary_max >= d.salary_min, {
    message: 'Maximum salary must be at least the minimum',
    path: ['salary_max'],
  })

export const applicationSchema = z.object({
  cover_letter: z.string().max(5000).optional(),
  resume_id: z.number().int().optional(),
  cv_id: z.number().int().optional(),
  answers: z.record(z.string(), z.unknown()).optional(),
})

export const candidateProfileSchema = z.object({
  headline: z.string().max(180).optional(),
  summary: z.string().max(5000).optional(),
  total_experience_months: z.number().int().min(0).max(720).optional(),
  expected_ctc: z.number().min(0).optional(),
  notice_period_days: z.number().int().min(0).max(365).optional(),
  visibility: z.enum(['public', 'recruiters', 'hidden']).optional(),
  is_open_to_work: z.boolean().optional(),
  skill_ids: z.array(z.number().int()).max(50).optional(),
})

export const employerRegisterSchema = z
  .object({
    first_name: z.string().min(1, 'Enter your full name').max(80),
    last_name: z.string().max(80).optional(),
    company_name: z.string().min(1, 'Enter your company name').max(180),
    email: z.string().email('Enter a valid work email address'),
    phone: z.string().min(5, 'Enter your contact phone number').max(32),
    website: z.string().url('Enter a valid website URL').optional().or(z.literal('')),
    size: z.enum(['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+']).optional(),
    industry_id: z.number().int().optional(),
    password: z
      .string()
      .min(10, 'Use at least 10 characters')
      .regex(/[A-Za-z]/, 'Include at least one letter')
      .regex(/[0-9]/, 'Include at least one number'),
    password_confirmation: z.string(),
    terms_accepted: z.literal(true, {
      errorMap: () => ({ message: 'Accept the terms to continue' }),
    }),
  })
  .refine((d) => d.password === d.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  })

export const companyVerificationSchema = z.object({
  document_type: z.enum(['gstin_certificate', 'certificate_of_incorporation', 'tax_id', 'utility_bill', 'business_license']),
  file: z.any().refine((f) => f !== null && f !== undefined, 'Select a verification document file'),
  notes: z.string().max(1000).optional(),
})

export const interviewScheduleSchema = z.object({
  title: z.string().min(1, 'Enter interview title/round name').max(180),
  mode: z.enum(['video', 'phone', 'in_person']),
  location_or_link: z.string().max(255).optional(),
  scheduled_at: z.string().min(1, 'Select date and time'),
  duration_minutes: z.number().int().min(15).max(480).default(60),
  panelist_ids: z.array(z.number().int()).optional(),
  notes: z.string().max(5000).optional(),
})

export const interviewFeedbackSchema = z.object({
  rating: z.number().int().min(1).max(5),
  recommendation: z.enum(['strong_yes', 'yes', 'neutral', 'no', 'strong_no']),
  scores: z.record(z.string(), z.number()).optional(),
  comments: z.string().min(1, 'Enter interview evaluation comments').max(5000),
})

export const offerCreateSchema = z.object({
  salary: z.number().min(0, 'Enter offered salary'),
  currency: z.string().length(3).default('INR'),
  joining_date: z.string().optional(),
  notes: z.string().max(5000).optional(),
})

export const offerDecisionSchema = z.object({
  response: z.enum(['accepted', 'declined']),
  reason: z.string().max(2000).optional(),
})

export const talentPoolCreateSchema = z.object({
  name: z.string().min(1, 'Enter talent pool name').max(120),
  description: z.string().max(1000).optional(),
})

export const talentPoolAddCandidateSchema = z.object({
  candidate_profile_id: z.number().int(),
  talent_pool_id: z.number().int(),
})

export const candidateNoteCreateSchema = z.object({
  note: z.string().min(1, 'Enter recruiter note').max(5000),
  mentions: z.array(z.string()).optional(),
})

export const candidateTagCreateSchema = z.object({
  tag: z.string().min(1, 'Enter tag').max(64),
})

export const candidatePrivacySchema = z.object({
  visibility: z.enum(['public', 'recruiters', 'applied_only', 'hidden', 'private']),
  availability_status: z.enum(['actively_looking', 'open_to_offers', 'not_looking']),
  hidden_company_ids: z.array(z.number().int()).optional(),
})

export const jobAlertSchema = z.object({
  name: z.string().min(1, 'Enter alert name').max(120),
  criteria: z.object({
    q: z.string().optional(),
    category_id: z.number().int().optional(),
    location_id: z.number().int().optional(),
    work_mode: z.string().optional(),
    employment_type: z.string().optional(),
    min_salary: z.number().optional(),
  }),
  frequency: z.enum(['daily', 'weekly', 'instant']),
  channels: z.array(z.enum(['email', 'in_app'])).min(1, 'Select at least one channel'),
})

export const customPipelineSchema = z.object({
  name: z.string().min(1, 'Enter pipeline name').max(120),
  stages: z.array(
    z.object({
      name: z.string().min(1, 'Enter stage name').max(80),
      type: z.enum(['applied', 'screening', 'shortlisted', 'assessment', 'interview', 'on_hold', 'offer', 'hired', 'rejected', 'withdrawn', 'custom']),
      color: z.string().max(32).optional(),
      sla_days: z.number().int().min(1).max(90).optional(),
      is_terminal: z.boolean().default(false),
    })
  ).min(2, 'Pipeline must have at least 2 stages'),
})

export const bulkMoveSchema = z.object({
  application_ids: z.array(z.string()).min(1, 'Select at least one candidate'),
  to_stage_id: z.number().int(),
  note: z.string().max(1000).optional(),
})

export const bulkAssignSchema = z.object({
  application_ids: z.array(z.string()).min(1, 'Select at least one candidate'),
  assigned_recruiter_id: z.number().int().nullable().optional(),
  assigned_hiring_manager_id: z.number().int().nullable().optional(),
})

export const bulkRejectSchema = z.object({
  application_ids: z.array(z.string()).min(1, 'Select at least one candidate'),
  rejection_reason: z.string().min(1, 'Provide a rejection reason').max(2000),
})

export const bulkPoolSchema = z.object({
  candidate_profile_ids: z.array(z.number().int()).min(1, 'Select candidates'),
  talent_pool_id: z.number().int(),
})

export const reportSubmissionSchema = z.object({
  reportable_type: z.enum(['job', 'company', 'user']),
  reportable_id: z.union([z.number().int(), z.string()]),
  reason: z.enum(['fake_job', 'scam', 'asking_money', 'misleading', 'spam', 'duplicate', 'illegal', 'other']),
  details: z.string().max(3000).optional(),
})

export type LoginInput = z.infer<typeof loginSchema>
export type RegisterInput = z.infer<typeof registerSchema>
export type EmployerRegisterInput = z.infer<typeof employerRegisterSchema>
export type JobInput = z.infer<typeof jobSchema>
export type ApplicationInput = z.infer<typeof applicationSchema>
export type CandidateProfileInput = z.infer<typeof candidateProfileSchema>
export type InterviewScheduleInput = z.infer<typeof interviewScheduleSchema>
export type InterviewFeedbackInput = z.infer<typeof interviewFeedbackSchema>
export type OfferCreateInput = z.infer<typeof offerCreateSchema>
export type OfferDecisionInput = z.infer<typeof offerDecisionSchema>
export type TalentPoolCreateInput = z.infer<typeof talentPoolCreateSchema>
export type CandidatePrivacyInput = z.infer<typeof candidatePrivacySchema>
export type JobAlertInput = z.infer<typeof jobAlertSchema>
export type CustomPipelineInput = z.infer<typeof customPipelineSchema>
export type BulkMoveInput = z.infer<typeof bulkMoveSchema>
export type BulkAssignInput = z.infer<typeof bulkAssignSchema>
export type BulkRejectInput = z.infer<typeof bulkRejectSchema>
export type BulkPoolInput = z.infer<typeof bulkPoolSchema>
export type ReportSubmissionInput = z.infer<typeof reportSubmissionSchema>